<script setup lang="ts">
import { computed } from 'vue';
import { usePageContext } from 'vike-vue/usePageContext';
import type { LinkedPage, Page } from '@repo/types';
import { ExternalLink } from 'lucide-vue-next';
import { Breadcrumbs, BrowseCard, useAddress } from '@repo/ui';
import Header from '@/components/Header.vue';
import Footer from '@repo/ui/components/Footer.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const { getAddressPart } = useAddress()

const pageContext = usePageContext();

const page = computed(() => pageContext.data.page as LinkedPage);

const children = computed(() => {
  return [...(page.value?.children || [])].sort((a, b) => (b.stats?.count || 0) - (a.stats?.count || 0));
});

const groupedChildren = computed(() => {
  if (!children.value.length) return {};

  return children.value.reduce((acc, child) => {
    const group = (child.group?.[pageContext.locale] ?? child.group) || (child.level === 'prefecture' ? t('sections.browseByArea') : t('enums.homeType.other'));
    if (!acc[group]) acc[group] = [];
    acc[group].push(child);
    return acc;
  }, {} as Record<string, Page[]>);
});

const prefecture = computed(() => {
  if (page.value.level === 'city') {
    return page.value.parents.find(p => p.level === 'prefecture');
  }
  if (page.value.level === 'prefecture') {
    return page.value;
  }
  return null;
});

const city = computed(() => {
  if (page.value.level === 'city') {
    return page.value;
  }
  return null;
});

const cities = computed(() => children.value.filter(p => p.level === 'city'))
const lines = computed(() => children.value.filter(p => p.level === 'line'))
const stations = computed(() => children.value.filter(p => p.level === 'station'))
</script>

<template>
  <div class="flex flex-col min-h-screen bg-gray-50">
    <Header />
    <div class="max-w-[1400px] container mx-auto px-4 sm:px-6 py-8">
      <!-- Level 1: All Prefectures -->
      <template v-if="page.level === 'type'">
        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ t('sections.browseByArea') }}</h1>
          <p class="text-gray-600">{{ t('sections.selectPrefecture') }}</p>
        </div>

        <div v-for="(children, group) in groupedChildren" :key="group" class="mb-8">
          <h2 class="text-xl font-semibold text-gray-800 mb-4">{{ group }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <BrowseCard
              v-for="(child, index) in children"
              :key="child.slug"
              :page="child"
              :featured="index < 4"
            />
          </div>
        </div>
      </template>

      <!-- Level 2: Cities in Prefecture -->
      <template v-else-if="page.level === 'prefecture' || page.level === 'line'">
        <!-- Breadcrumb -->
        <Breadcrumbs class="mb-6" />

        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">
            {{ getAddressPart(page) }}
          </h1>
          <a
            v-if="page.data?.openMarketPath"
            :href="`https://market.openrm.jp/${page.data.openMarketPath}`"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors mb-4"
          >
            <img src="/assets/favicon.svg" class="w-3.5 h-3.5" alt="" />
            {{ t('property.view_livability_data') }}
          </a>
          <p v-if="typeof page.stats?.count === 'number'" class="text-gray-600">
            {{ page.stats?.count?.toLocaleString() }} {{ t('searchResults.properties') }}
            <template v-if="cities.length"> • {{ cities.length }} {{ t('sections.citiesAndWards').toLowerCase() }}</template>
            <template v-if="lines.length"> • {{ lines.length }} {{ t('sections.trainAndBus').toLowerCase() }}</template>
            <template v-if="stations.length"> • {{ stations.length }} {{ t('sections.stationsAndBus').toLowerCase() }}</template>
          </p>
        </div>

        <!-- View all listings button -->
        <a
          class="block bg-sky-50 border border-sky-200 rounded-lg p-6 mb-6 cursor-pointer
          hover:bg-sky-100 transition-colors"
          :href="'/' + (page.canonical ?? page.path) + '/search'"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <svg class="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <h3 class="font-semibold text-gray-900">
                  {{ t('sections.viewAllPropertiesIn', { place: getAddressPart(page) }) }}
                </h3>
                <p v-if="typeof page.stats?.count === 'number'" class="text-sm text-gray-600 mt-0.5">
                  {{ t('sections.listingsAvailable', { count: page.stats?.count?.toLocaleString() }) }}
                </p>
              </div>
            </div>
            <svg class="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </a>

        <!-- Cities -->
        <template
          v-for="{ children, label } of [
            { children: cities, label: t('sections.citiesAndWards') },
            { children: lines, label: t('sections.trainAndBus') },
            { children: stations, label: t('sections.stationsAndBus') },
          ].filter(item => item.children.length)"
          :key="label"
        >
          <h2 class="text-xl font-semibold text-gray-800 mb-4">{{ label }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <BrowseCard
              v-for="(child, index) in children"
              :key="child.slug"
              :page="child"
              :featured="index < 4"
            />
          </div>
        </template>
      </template>

      <!-- Level 3: Towns in City -->
      <template v-else-if="page.level === 'city'">
        <!-- Breadcrumb -->
        <Breadcrumbs class="mb-6" />

        <div class="mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">
            {{ getAddressPart(page) }}
          </h1>
          <a
            v-if="page.data?.openMarketPath"
            :href="`https://market.openrm.jp/${page.data.openMarketPath}`"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full hover:bg-gray-200 transition-colors mb-4"
          >
            <img src="/assets/favicon.svg" class="w-3.5 h-3.5" alt="" />
            {{ t('property.view_livability_data') }}
          </a>
          <p class="text-gray-600">
            <template v-if="typeof page.stats?.count === 'number'">{{ page.stats?.count?.toLocaleString() }} {{ t('searchResults.properties') }} •</template>
            {{ page.children.length }} {{ t('sections.neighborhoods').toLowerCase() }}
          </p>
        </div>

        <!-- View all city listings -->
        <a
          v-if="prefecture && city"
          class="block bg-sky-50 border border-sky-200 rounded-lg p-6 mb-6 cursor-pointer
          hover:bg-sky-100 transition-colors"
          :href="'/' + city.path + '/search'"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <svg class="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <h3 class="font-semibold text-gray-900">
                  {{ t('sections.viewAllPropertiesIn', { place: getAddressPart(page) }) }}
                </h3>
                <p v-if="typeof page.stats?.count === 'number'" class="text-sm text-gray-600 mt-0.5">
                  {{ t('sections.listingsAvailable', { count: page.stats?.count?.toLocaleString() }) }}
                </p>
              </div>
            </div>
            <svg class="h-5 w-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </a>

        <!-- Towns/Neighborhoods -->
        <template v-if="page.children.length > 0">
          <h2 class="text-xl font-semibold text-gray-800 mb-4">{{ t('sections.neighborhoods') }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <BrowseCard
              v-for="(child, index) in page.children"
              :key="child.slug"
              :page="child"
              :featured="index < 4"
            />
          </div>
        </template>
      </template>

      <!-- Error state -->
      <template v-else>
        <div class="text-center py-12">
          <p class="text-gray-600">Location not found</p>
        </div>
      </template>
    </div>
    <Footer />
  </div>
</template>
